import type {
  FeatureMatrixRow,
  Plan,
  Subscription,
  TenantAccountStats,
  TenantDetail,
} from '@up2d8/contracts';

/**
 * Abonnementen en upsell in Master (MST-6): de rekensom en de signalen, los van het scherm.
 *
 * Hier en niet in de API: de cijfers komen uit twee modules (`tenants` voor organisaties, pakketten
 * en functies, `identity` voor de accounts), en geen van beide mag de andere kennen (ADR-0016). Het
 * scherm haalt de drie lijsten op en legt ze hier naast elkaar. Alles is een zuivere functie, zodat
 * `billing.test.ts` de regels vastlegt.
 */

/** Wat een organisatie per maand betaalt, in eurocent. Alleen een actief abonnement telt. */
export function monthlyCents(
  subscription: Subscription | undefined,
  plan: Plan | undefined,
  activeUsers: number,
): number {
  if (!subscription || !plan || subscription.status !== 'active') return 0;
  const bruto = plan.pricePerMonthCents + plan.pricePerUserCents * activeUsers;
  return Math.round((bruto * (100 - subscription.discountPct)) / 100);
}

export type SignalKind =
  'noPlan' | 'overLimit' | 'nearLimit' | 'outsidePlan' | 'growing' | 'engaged' | 'trialEnding';

export interface Signal {
  kind: SignalKind;
  /** Zwaarder = hoger in de lijst. */
  weight: number;
  /** Getallen voor de zin op het scherm. */
  values: Record<string, string | number>;
}

export interface TenantBilling {
  tenant: TenantDetail;
  subscription: Subscription | undefined;
  plan: Plan | undefined;
  stats: TenantAccountStats | undefined;
  /** Eurocent per maand, na korting; 0 zonder actief abonnement. */
  mrrCents: number;
  /** Bezetting van het pakket: actieve accounts gedeeld door het maximum; `null` = onbeperkt. */
  usage: number | null;
  signals: Signal[];
  /** Het pakket dat beter past, als er een is. */
  suggestedPlan: Plan | undefined;
}

/** Drempels van de analyse, op één plek. */
export const THRESHOLDS = {
  nearLimit: 0.8,
  growingShare: 0.2,
  growingMin: 5,
  engagedShare: 0.6,
  engagedMin: 5,
  trialDays: 14,
} as const;

const DAY = 24 * 60 * 60 * 1000;

/**
 * Per organisatie: wat ze betaalt, hoe vol haar pakket zit, en waarom ze een gesprek waard is.
 *
 * - geen abonnement;
 * - boven of dicht bij het maximum aantal gebruikers;
 * - functies aan die niet in haar pakket zitten (maar wel in een groter);
 * - snel groeiend: veel nieuwe accounts in de laatste 30 dagen;
 * - sterk gebruik: een groot deel meldde zich de laatste 7 dagen aan;
 * - een proefperiode die binnenkort afloopt.
 */
export function analyse(
  tenants: TenantDetail[],
  plans: Plan[],
  subscriptions: Subscription[],
  stats: TenantAccountStats[],
  matrix: FeatureMatrixRow[],
  now = new Date(),
): TenantBilling[] {
  return analyseAll(
    tenants.filter((tenant) => tenant.status !== 'archived'),
    plans,
    subscriptions,
    stats,
    matrix,
    now,
  );
}

/**
 * Als `analyse`, maar mét het archief. Voor de export met filter "Gearchiveerd": die bleef anders
 * leeg. Cijfers en signalen rekenen met `analyse`, zodat het archief daar niet meetelt.
 */
export function analyseAll(
  tenants: TenantDetail[],
  plans: Plan[],
  subscriptions: Subscription[],
  stats: TenantAccountStats[],
  matrix: FeatureMatrixRow[],
  now = new Date(),
): TenantBilling[] {
  const planById = new Map(plans.map((plan) => [plan.id, plan]));
  const subByTenant = new Map(subscriptions.map((sub) => [sub.tenantId, sub]));
  const statsByTenant = new Map(stats.map((row) => [row.tenantId, row]));
  const flagsByTenant = new Map(matrix.map((row) => [row.tenantId, row.values]));
  const ordered = [...plans]
    .filter((plan) => plan.active)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const allPaidFlags = new Set(ordered.flatMap((plan) => plan.includedFlags));

  return tenants.map((tenant) => {
    const subscription = subByTenant.get(tenant.id);
    const plan = subscription ? planById.get(subscription.planId) : undefined;
    const row = statsByTenant.get(tenant.id);
    const active = row?.active ?? 0;
    const usage = plan?.maxUsers ? active / plan.maxUsers : null;
    const flags = flagsByTenant.get(tenant.id) ?? {};
    const usedFlags = Object.entries(flags)
      .filter(([key, on]) => on && allPaidFlags.has(key))
      .map(([key]) => key);
    const outside = plan ? usedFlags.filter((key) => !plan.includedFlags.includes(key)) : [];

    const signals: Signal[] = [];
    if (!subscription || subscription.status === 'cancelled') {
      signals.push({ kind: 'noPlan', weight: 50, values: { users: active } });
    }
    if (usage !== null && usage > 1) {
      signals.push({
        kind: 'overLimit',
        weight: 100,
        values: { users: active, max: plan?.maxUsers ?? 0 },
      });
    } else if (usage !== null && usage >= THRESHOLDS.nearLimit) {
      signals.push({
        kind: 'nearLimit',
        weight: 80,
        values: { users: active, max: plan?.maxUsers ?? 0, pct: Math.round(usage * 100) },
      });
    }
    if (outside.length > 0) {
      signals.push({
        kind: 'outsidePlan',
        weight: 70,
        values: { count: outside.length, flags: outside.join(', ') },
      });
    }
    if (
      row &&
      row.new30d >= THRESHOLDS.growingMin &&
      row.total > 0 &&
      row.new30d / row.total >= THRESHOLDS.growingShare
    ) {
      signals.push({
        kind: 'growing',
        weight: 60,
        values: { added: row.new30d, pct: Math.round((row.new30d / row.total) * 100) },
      });
    }
    if (
      row &&
      row.active >= THRESHOLDS.engagedMin &&
      row.active7d / row.active >= THRESHOLDS.engagedShare
    ) {
      signals.push({
        kind: 'engaged',
        weight: 40,
        values: { pct: Math.round((row.active7d / row.active) * 100) },
      });
    }
    if (subscription?.status === 'trial' && subscription.trialEndsAt) {
      const days = Math.ceil((Date.parse(subscription.trialEndsAt) - now.getTime()) / DAY);
      if (days <= THRESHOLDS.trialDays) {
        signals.push({ kind: 'trialEnding', weight: 90, values: { days: Math.max(days, 0) } });
      }
    }
    signals.sort((a, b) => b.weight - a.weight);

    return {
      tenant,
      subscription,
      plan,
      stats: row,
      mrrCents: monthlyCents(subscription, plan, active),
      usage,
      signals,
      suggestedPlan: suggest(ordered, plan, active, usedFlags),
    };
  });
}

/**
 * Het kleinste pakket boven het huidige waarin de gebruikers passen en de gebruikte functies
 * zitten. Zonder pakket: het kleinste dat past. `undefined` als het huidige al past.
 */
export function suggest(
  ordered: Plan[],
  current: Plan | undefined,
  users: number,
  usedFlags: string[],
): Plan | undefined {
  const fits = (plan: Plan) =>
    (plan.maxUsers === null || users <= plan.maxUsers * THRESHOLDS.nearLimit) &&
    usedFlags.every((key) => plan.includedFlags.includes(key));
  if (current && fits(current)) return undefined;
  const candidates = current
    ? ordered.filter((plan) => plan.sortOrder > current.sortOrder)
    : ordered;
  return candidates.find(fits) ?? (current ? candidates[candidates.length - 1] : candidates[0]);
}

export interface BillingTotals {
  mrrCents: number;
  arrCents: number;
  paying: number;
  trials: number;
  withoutPlan: number;
  averageCents: number;
  perPlan: { plan: Plan; tenants: number; mrrCents: number }[];
}

export function totals(rows: TenantBilling[], plans: Plan[]): BillingTotals {
  const mrrCents = rows.reduce((sum, row) => sum + row.mrrCents, 0);
  const paying = rows.filter((row) => row.mrrCents > 0).length;
  return {
    mrrCents,
    arrCents: mrrCents * 12,
    paying,
    trials: rows.filter((row) => row.subscription?.status === 'trial').length,
    withoutPlan: rows.filter((row) => !row.subscription || row.subscription.status === 'cancelled')
      .length,
    averageCents: paying > 0 ? Math.round(mrrCents / paying) : 0,
    perPlan: [...plans]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((plan) => {
        const mine = rows.filter((row) => row.plan?.id === plan.id);
        return {
          plan,
          tenants: mine.length,
          mrrCents: mine.reduce((sum, row) => sum + row.mrrCents, 0),
        };
      }),
  };
}

/** Eurocent als bedrag, zonder centen als het een rond bedrag is: "€ 1.250" of "€ 49,50". */
export function euro(cents: number, locale = 'nl-BE'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/** Eén CSV-veld: tussen aanhalingstekens als het een scheidingsteken, aanhalingsteken of regel bevat. */
function csvField(value: string | number): string {
  const text = String(value);
  return /[;"\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * De organisatielijst van Master als CSV (MST-5), met een puntkomma: zo opent Excel het in een
 * Belgische of Nederlandse instelling meteen in kolommen. Bedragen in euro, met een komma.
 */
export function organisationsCsv(rows: TenantBilling[]): string {
  const header = [
    'naam',
    'code',
    'status',
    'type',
    'pakket',
    'abonnement',
    'accounts',
    'actief',
    'actief_7d',
    'nieuw_30d',
    'mrr_eur',
    'signalen',
  ];
  const lines = rows.map((row) =>
    [
      row.tenant.name,
      row.tenant.code,
      row.tenant.status,
      row.tenant.orgType,
      row.plan?.key ?? '',
      row.subscription?.status ?? '',
      row.stats?.total ?? 0,
      row.stats?.active ?? 0,
      row.stats?.active7d ?? 0,
      row.stats?.new30d ?? 0,
      (row.mrrCents / 100).toFixed(2).replace('.', ','),
      row.signals.map((signal) => signal.kind).join(' '),
    ]
      .map(csvField)
      .join(';'),
  );
  return [header.join(';'), ...lines].join('\n') + '\n';
}
